import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Building2, Clock, MapPin, Plug, Users, X } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDate } from "../../utils";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import ListFilters from "../../components/ui/ListFilters";
import Pagination from "../../components/ui/Pagination";
import Skeleton from "../../components/ui/Skeleton";
import { useToast } from "../../components/ui/Toast";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";
import {
  AppStack,
  CopyId,
  Count,
  DetailsState,
  Drawer,
  DrawerLoading,
  InfoGrid,
  InstallList,
  RecordLinks,
  Section,
  ago,
  useOpenParam,
} from "../../components/directory/shared";

/**
 * The locations (CRM sub-accounts) using our sub-account apps: where they are,
 * which apps they run, and how busy they are.
 */

const DEFAULTS = { page: 1, limit: 20, appId: "", companyId: "", country: "", details: "", sort: "newest", q: "" };

const locationLabel = (l) => l?.name || `Location ${l?.locationId || ""}`.trim();
const place = (l) => [l?.city, l?.state, l?.country].filter(Boolean).join(", ");

const LocationDrawer = ({ id, onClose }) => {
  const { directory } = useAppServices();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setData(null);
    setError("");
    directory.Location(id).then(({ response, error: err }) => {
      if (!alive) return;
      if (err || !response) setError(err?.message || "Could not load this location.");
      else setData(response);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const l = data?.location;
  const a = data?.activity;

  return (
    <Drawer
      onClose={onClose}
      label="Location details"
      header={
        l ? (
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-ink-100 text-ink-500">
              <MapPin size={20} />
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-[16px] font-semibold tracking-tight text-ink-900">{locationLabel(l)}</h2>
              {place(l) && <p className="truncate text-[12.5px] text-ink-500">{place(l)}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <DetailsState record={l} />
              </div>
            </div>
          </div>
        ) : (
          <Skeleton className="h-11 w-48" />
        )
      }
    >
      {error ? (
        <p className="m-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{error}</p>
      ) : !data ? (
        <DrawerLoading />
      ) : (
        <>
          <InfoGrid
            rows={[
              ["Location ID", <CopyId value={l.locationId} />],
              [
                "Company",
                data.company ? (
                  <Link to={`/companies?open=${data.company._id}`} className="font-medium text-brand-600 hover:underline">
                    {data.company.name || data.company.companyId}
                  </Link>
                ) : (
                  l.companyId && <CopyId value={l.companyId} />
                ),
              ],
              ["Email", l.email],
              ["Phone", l.phone],
              [
                "Website",
                l.website && (
                  <a href={/^https?:\/\//i.test(l.website) ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">
                    {l.website}
                  </a>
                ),
              ],
              ["Address", [l.address, l.city, l.state, l.postalCode, l.country].filter(Boolean).join(", ")],
              ["Timezone", l.timezone],
              ["First seen", formatDate(l.createdAt)],
              ["Last attempt", !l.lastSyncedAt && l.syncError ? l.syncError : null],
            ]}
          />
          <Section title={`Activity · last ${a.days || 30} days`} icon={Activity}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Count label="Messages" value={a.conversation.ok} />
              <Count label="Message errors" value={a.conversation.failed} tone="rose" />
              <Count label="Actions run" value={a.action.ok} />
              <Count label="Action errors" value={a.action.failed} tone="rose" />
            </div>
            {(a.conversation.warnings || 0) + (a.action.warnings || 0) > 0 && (
              <p className="mt-2 text-[12px] text-amber-700">
                {(a.conversation.warnings || 0) + (a.action.warnings || 0)} ignored — replies sent to another of the customer's numbers. Warnings, not errors.
              </p>
            )}
            <p className="mt-2 text-[12px] text-ink-400">Last activity: {a.lastAt ? ago(a.lastAt) : "never"}</p>
          </Section>
          <Section title={`Apps · ${data.installs.length}`} icon={Plug}>
            <InstallList installs={data.installs} showAccount={false} />
          </Section>
          <Section title="Installed by" icon={Users}>
            <RecordLinks
              items={data.users}
              to={(u) => `/users?open=${u._id}`}
              empty="Not known yet."
              render={(u) => (
                <>
                  <span className="min-w-0 flex-1 truncate font-medium text-ink-800">{u.name || u.userId}</span>
                  <span className="shrink-0 truncate text-[11.5px] text-ink-400">{u.email}</span>
                </>
              )}
            />
          </Section>
        </>
      )}
    </Drawer>
  );
};

const Locations = () => {
  const { directory, installs } = useAppServices();
  const toast = useToast();

  const [query, setQuery] = useListQuery(DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }));
  const [openId, setOpenId] = useOpenParam();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: DEFAULTS.limit, facets: { countries: [] } });
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: query.page, limit: query.limit, sort: query.sort });
    for (const key of ["appId", "companyId", "country", "details", "q"]) if (query[key]) params.set(key, query[key]);
    const { response, error } = await directory.Locations(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load locations", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, limit: query.limit, facets: { countries: [] } };
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setData(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.appId, query.companyId, query.country, query.details, query.sort, query.q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    installs.Stats().then(({ response }) => setApps(response?.byApp || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = Boolean(query.q || query.appId || query.companyId || query.country || query.details);
  const close = useCallback(() => setOpenId(null), [setOpenId]);
  const companyName = data.items.find((l) => l.companyId === query.companyId)?.companyName;

  return (
    <div className="space-y-5">
      {query.companyId && (
        <div className="flex items-center gap-2 text-[13px] text-ink-600">
          <Building2 size={14} className="text-ink-400" />
          Showing locations of <span className="font-semibold text-ink-900">{companyName || query.companyId}</span>
          <button type="button" onClick={() => setQuery({ companyId: "" })} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-800">
            <X size={12} /> Show all
          </button>
        </div>
      )}

      <Card padded={false}>
        <ListFilters
          search={search}
          onSearch={setSearch}
          placeholder="Search name, email, phone, city or ID"
          filters={[
            { label: "App", value: query.appId, onChange: (appId) => setQuery({ appId }), options: [["", "All apps"], ...apps.map((a) => [a.appId, a.appName])], className: "w-48" },
            { label: "Country", value: query.country, onChange: (country) => setQuery({ country }), options: [["", "Any country"], ...(data.facets?.countries || []).map((c) => [c, c])], className: "w-36" },
            { label: "Details", value: query.details, onChange: (details) => setQuery({ details }), options: [["", "Any details"], ["synced", "Details fetched"], ["pending", "Details pending"]], className: "w-40" },
            { label: "Sort", value: query.sort === "newest" ? "" : query.sort, onChange: (sort) => setQuery({ sort: sort || "newest" }), options: [["", "Newest first"], ["name", "Name"]], className: "w-36" },
          ]}
          onClear={() => setQuery({ q: "", appId: "", companyId: "", country: "", details: "" })}
        />

        {loading ? (
          <div className="divide-y divide-ink-150">
            {[0, 1, 2, 3].map((k) => (
              <div key={k} className="flex items-center gap-3 px-5 py-4">
                <Skeleton className="h-9 w-9" rounded="rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={<MapPin size={24} />}
            title={filtered ? "No locations match" : "No locations yet"}
            description={filtered ? "Try another app, country or search term." : "Locations appear here as your sub-account apps are installed."}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[56rem] text-sm">
              <thead>
                <tr className="border-b border-ink-150 text-left">
                  {["Location", "Company", "Where", "Apps", "Last activity", "First seen"].map((h) => (
                    <th key={h} className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-150">
                {data.items.map((l) => (
                  <tr key={l._id} onClick={() => setOpenId(l._id)} className={cx("cursor-pointer transition-colors hover:bg-ink-50", openId === l._id && "bg-brand-50/50")}>
                    <td className="max-w-[16rem] px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-ink-100 text-ink-500">
                          <MapPin size={15} />
                        </span>
                        <div className="min-w-0">
                          <button type="button" onClick={() => setOpenId(l._id)} className="block max-w-full truncate text-left font-medium text-ink-900 hover:text-brand-600">
                            {locationLabel(l)}
                          </button>
                          {l.name ? <CopyId value={l.locationId} /> : <DetailsState record={l} />}
                        </div>
                      </div>
                    </td>
                    <td className="max-w-[12rem] px-5 py-3" onClick={(e) => e.stopPropagation()}>
                      {l.companyId ? (
                        <button type="button" onClick={() => setQuery({ companyId: l.companyId })} className="block max-w-full truncate text-left text-[13px] text-ink-700 hover:text-brand-600" title="Show this company's locations">
                          {l.companyName || l.companyId}
                        </button>
                      ) : (
                        <span className="text-ink-300">—</span>
                      )}
                    </td>
                    <td className="max-w-[12rem] px-5 py-3">
                      <p className="truncate text-[13px] text-ink-700">{place(l) || <span className="text-ink-300">—</span>}</p>
                      {l.timezone && (
                        <p className="flex items-center gap-1 truncate text-[11.5px] text-ink-400">
                          <Clock size={10} /> {l.timezone}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <AppStack apps={l.apps} />
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-ink-500">{l.lastActivityAt ? ago(l.lastActivityAt) : <span className="text-ink-300">Never</span>}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-ink-500">{formatDate(l.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            noun={data.total === 1 ? "location" : "locations"}
            onPage={(page) => setQuery({ page })}
            onLimit={(limit) => setQuery({ limit })}
          />
        )}
      </Card>

      {openId && <LocationDrawer id={openId} onClose={close} />}
    </div>
  );
};

export default Locations;
